import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import IndexedDb from "../../Services/IndexedDb/IndexedDb";
import swal from "../SweetAlert2";

// 로그인 후에만 접근 가능한 페이지 목록
const privatePages = ["/selected-company", "/selected-site", "/composed-education", "/watch-education", "/health-interview", "/health-document", "/construction-test", "/my-calendar"];

// 개인정보 동의 및 사진 등록이 필요한 페이지 목록
const concentRequiredPages = ["/selected-company", "/selected-site", "/my-calendar", "/watch-education", "/composed-education", "/health-interview", "/health-document", "/construction-test"];

// 로그인 전에만 접근 가능한 페이지 목록
const publicOnlyPages = ["/login", "/signup", "/m-login"];

function AuthGuard({ children, path }) {
  const { authorization } = useSelector((state) => state.localStorage);
  const isLoggedIn = authorization.auth;
  const authType = authorization.authType;
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userPrivacyAgreement, setUserPrivacyAgreement] = useState(undefined);
  const [userPhoto, setUserPhoto] = useState(undefined);
  const alertedRef = useRef(false);

  // 로그아웃 상태 감지
  useEffect(() => {
    // authorization.auth가 false로 변경되면 로그아웃 중으로 간주
    if (!isLoggedIn) {
      setIsLoggingOut(true);
      // 1초 후 로그아웃 상태 해제 (페이지 이동 완료 후)
      const timer = setTimeout(() => {
        setIsLoggingOut(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn]);

  // 사용자 동의 여부 및 사진 등록 여부 로드
  useEffect(() => {
    IndexedDb.GetUserInfo()
      .then((data) => {
        if (data) {
          // 개인정보 동의 여부 체크 (concent가 개인정보동의방침)
          if (typeof data.concent !== "undefined") {
            setUserPrivacyAgreement(data.concent === true);
          } else {
            setUserPrivacyAgreement(undefined);
          }
          
          // 사진 등록 여부 체크
          const hasPhoto = data.photoUrl && data.photoUrl.trim() !== "";
          setUserPhoto(hasPhoto);
        } else {
          setUserPrivacyAgreement(undefined);
          setUserPhoto(undefined);
        }
      })
      .catch(() => {
        setUserPrivacyAgreement(undefined);
        setUserPhoto(undefined);
      });
  }, []);

  // 로그아웃 중일 때는 권한 체크를 건너뛰고 children을 렌더링
  if (isLoggingOut) {
    return children;
  }

  // 로그인 후에만 접근 가능한 페이지인데 로그인하지 않은 경우 → 로그인 페이지로 유도
  if (privatePages.includes(path) && !isLoggedIn) {
    return <Navigate to='/login' replace />;
  }

  // 개인정보 동의 및 사진 등록 필요 페이지 차단 로직: 사용자 정보가 로딩된 이후에만 검사
  if (
    isLoggedIn &&
    authType !== "b" && // 관리자는 개인정보 동의 및 사진 등록 필요없음
    concentRequiredPages.includes(path) &&
    userPrivacyAgreement !== undefined &&
    userPhoto !== undefined
  ) {
    const needsPrivacyAgreement = userPrivacyAgreement !== true;
    const needsPhoto = userPhoto !== true;
    
    if (needsPrivacyAgreement || needsPhoto) {
      if (!alertedRef.current) {
        alertedRef.current = true;
        
        let message = "";
        if (needsPrivacyAgreement && needsPhoto) {
          message = "개인정보처리방침 동의와 프로필 사진 등록이 필요합니다.";
        } else if (needsPrivacyAgreement) {
          message = "개인정보처리방침에 동의가 필요합니다.";
        } else if (needsPhoto) {
          message = "프로필 사진 등록이 필요합니다.";
        }
        
        swal.info(message, "마이페이지에서 완료해주세요.");
      }
      return <Navigate to='/my-page' replace />;
    }
  }

  // 로그인 전에만 접근 가능한 페이지인데 이미 로그인한 경우 → 메인으로 이동
  if (publicOnlyPages.includes(path) && isLoggedIn) {
    return <Navigate to='/' replace />;
  }

  // 권한이 있는 경우 해당 페이지 렌더링
  return children;
}

export default AuthGuard;
